import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Loader2, Receipt } from 'lucide-react';
import { getAiqInvoicePreview, sendAiqInvoice, refreshAiqSync, setProductInvoiceBasis } from '../../lib/api';
import { errMsg, aiqSentPatch } from './aiqMeta';
import { InvoiceSummaryStrip, InvoiceNotices, InvoiceTotals, NotSupplied } from './InvoicePreviewParts';
import { InvoiceLinesTable } from './InvoiceLinesTable';
import { InvoiceSentPanel, InvoicePreviewFooter } from './InvoiceSentPanel';

const usePreview = (id, open) => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      setPreview((await getAiqInvoicePreview(id)).data);
    } catch (e) {
      toast.error(errMsg(e, 'Could not build the invoice preview'));
    } finally {
      setLoading(false);
    }
  }, [id]);
  useEffect(() => { if (open) { setPreview(null); load(); } }, [open, load]);
  return { preview, loading, load };
};

export const InvoicePreviewDialog = ({ open, onOpenChange, dispatch, onSent }) => {
  const id = dispatch?.id;
  const { preview, loading, load } = usePreview(id, open);
  const [busy, setBusy] = useState(null);
  const [sent, setSent] = useState(null);

  useEffect(() => { if (open) setSent(null); }, [open]);

  const run = async (kind, fn, fallback) => {
    setBusy(kind);
    try {
      await fn();
    } catch (e) {
      toast.error(errMsg(e, fallback));
      if ([400, 409].includes(e?.response?.status)) await load();
    } finally {
      setBusy(null);
    }
  };

  const refresh = () => run('refresh', async () => {
    await refreshAiqSync();
    toast.success('Prices refreshed from AccountsIQ');
    await load();
  }, 'Refresh from AccountsIQ failed');

  const changeBasis = (line, basis) => run('basis', async () => {
    await setProductInvoiceBasis(line.product_id, basis);
    await load();
  }, 'Could not save how this product is invoiced');

  const send = () => run('send', async () => {
    const { data } = await sendAiqInvoice(id, preview.preview_hash);
    setSent(data);
    onSent?.(id, aiqSentPatch(data));
  }, 'Sending to AccountsIQ failed');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="aiq-preview-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <Receipt className="w-5 h-5 text-[#005F73]" />
            {sent ? 'Sent to AccountsIQ' : 'Invoice preview'} · <span className="font-mono">{dispatch?.dispatch_number}</span>
          </DialogTitle>
          <DialogDescription className="text-xs">
            {sent ? 'The draft invoice is waiting in AccountsIQ for you to post.'
              : 'Check what was shipped against what will be invoiced. Nothing goes to AccountsIQ until you press Send.'}
          </DialogDescription>
        </DialogHeader>
        {sent ? (
          <InvoiceSentPanel result={sent} dispatchNumber={dispatch?.dispatch_number} onDone={() => onOpenChange(false)} />
        ) : !preview ? (
          <div className="flex items-center gap-2 py-16 justify-center text-sm text-zinc-500" data-testid="aiq-preview-loading">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Building preview from AccountsIQ prices…</> : 'Preview unavailable.'}
          </div>
        ) : (
          <div className={`space-y-4 transition-opacity duration-200 ${loading ? 'opacity-60' : ''}`}>
            <InvoiceSummaryStrip preview={preview} onRefresh={refresh} refreshing={busy === 'refresh'} />
            <InvoiceNotices preview={preview} />
            <InvoiceLinesTable lines={preview.lines} currency={preview.totals?.currency} onBasis={changeBasis}
              busy={!!busy || !preview.user_can_send} />
            <div className="flex flex-wrap items-start justify-between gap-4">
              <NotSupplied items={preview.not_supplied} />
              <InvoiceTotals totals={preview.totals} />
            </div>
            <InvoicePreviewFooter preview={preview} sending={busy === 'send'} disabled={!!busy || loading}
              onSend={send} onClose={() => onOpenChange(false)} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
